import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiSave, FiX, FiInfo, FiEdit2, FiRefreshCw, FiArrowLeft } from 'react-icons/fi';
import DashboardLayout from '../Dashboard/DashboardLayout';
import * as variablesApi from '../../api/variables';
import * as environmentsApi from '../../api/environments';
import * as architecturesApi from '../../api/architectures';

interface Environment {
  id: string;
  name: string;
  displayName?: string;
  type: string;
  color?: string;
}

interface Architecture {
  id: string;
  name: string;
  description?: string;
}

interface ParameterWithOverride extends variablesApi.ArchitectureVariable {
  hasOverride: boolean;
  overrideValue?: any;
  overrideId?: string;
}

const EnvironmentVariablesPage: React.FC = () => {
  const { environmentId } = useParams<{ environmentId: string }>();
  const navigate = useNavigate();

  const [environment, setEnvironment] = useState<Environment | null>(null);
  const [architectures, setArchitectures] = useState<Architecture[]>([]);
  const [selectedArchitectureId, setSelectedArchitectureId] = useState<string>('');
  const [parameters, setParameters] = useState<ParameterWithOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingValues, setEditingValues] = useState<Record<string, any>>({});

  useEffect(() => {
    if (environmentId) {
      loadData();
    }
  }, [environmentId]);

  useEffect(() => {
    if (selectedArchitectureId) {
      loadParameters();
    }
  }, [selectedArchitectureId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Charger l'environnement
      const envs = await environmentsApi.listEnvironments();
      const env = envs.find(e => e.id === environmentId);
      if (env) {
        setEnvironment(env);
      }

      // Charger les architectures (toutes, pas seulement les 10 récentes)
      const archs = await architecturesApi.getAllArchitectures();
      setArchitectures(archs);

      if (archs.length > 0 && !selectedArchitectureId) {
        setSelectedArchitectureId(archs[0].id);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      alert('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const loadParameters = async () => {
    if (!selectedArchitectureId || !environmentId) return;

    try {
      setLoading(true);

      // Charger les paramètres de l'architecture (scope=parameter uniquement)
      const variables = await variablesApi.getArchitectureVariables(
        selectedArchitectureId,
        'parameter'
      );

      // Charger les overrides existants pour cet environnement
      const overrides = await variablesApi.getEnvironmentOverrides(environmentId);

      // Fusionner les données
      const parametersWithOverrides: ParameterWithOverride[] = variables.map(v => {
        const override = overrides.find(o => o.variable_id === v.id);
        return {
          ...v,
          hasOverride: !!override,
          overrideValue: override?.value,
          overrideId: override?.id
        };
      });

      setParameters(parametersWithOverrides);

      // Initialiser editingValues avec les valeurs actuelles
      const initialValues: Record<string, any> = {};
      parametersWithOverrides.forEach(p => {
        if (p.hasOverride) {
          initialValues[p.id] = p.overrideValue;
        }
      });
      setEditingValues(initialValues);
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      alert('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (variableId: string, value: any) => {
    setEditingValues(prev => ({
      ...prev,
      [variableId]: value
    }));
  };

  const handleSaveOverride = async (variableId: string) => {
    if (!environmentId) return;

    try {
      setSaving(true);
      const value = editingValues[variableId];

      await variablesApi.setEnvironmentOverride(environmentId, variableId, value);

      // Recharger les paramètres pour mettre à jour l'UI
      await loadParameters();

      alert('✅ Override sauvegardé avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde de l\'override');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveOverride = async (variableId: string) => {
    if (!environmentId) return;

    try {
      setSaving(true);

      await variablesApi.removeEnvironmentOverride(environmentId, variableId);

      // Supprimer la valeur d'édition
      setEditingValues(prev => {
        const updated = { ...prev };
        delete updated[variableId];
        return updated;
      });

      // Recharger les paramètres
      await loadParameters();

      alert('✅ Override supprimé avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('❌ Erreur lors de la suppression de l\'override');
    } finally {
      setSaving(false);
    }
  };

  const renderValueInput = (param: ParameterWithOverride) => {
    const currentValue = editingValues[param.id] !== undefined
      ? editingValues[param.id]
      : param.hasOverride
        ? param.overrideValue
        : '';

    if (param.type === 'boolean') {
      return (
        <select
          value={String(currentValue)}
          onChange={(e) => handleValueChange(param.id, e.target.value === 'true')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
        >
          <option value="">Utiliser la valeur par défaut</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    if (param.type === 'number') {
      return (
        <input
          type="number"
          value={currentValue}
          onChange={(e) => handleValueChange(param.id, e.target.value ? Number(e.target.value) : '')}
          placeholder={`Défaut: ${param.default_value?.value || 'non défini'}`}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
        />
      );
    }

    if (param.type === 'select' && param.validation_rules?.enum) {
      return (
        <select
          value={currentValue}
          onChange={(e) => handleValueChange(param.id, e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
        >
          <option value="">Utiliser la valeur par défaut</option>
          {param.validation_rules.enum.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    if (param.type === 'json') {
      return (
        <textarea
          value={typeof currentValue === 'string' ? currentValue : JSON.stringify(currentValue, null, 2)}
          onChange={(e) => handleValueChange(param.id, e.target.value)}
          placeholder={`Défaut: ${JSON.stringify(param.default_value?.value || {})}`}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white font-mono text-sm"
        />
      );
    }

    // String par défaut
    return (
      <input
        type="text"
        value={currentValue}
        onChange={(e) => handleValueChange(param.id, e.target.value)}
        placeholder={`Défaut: ${param.default_value?.value || 'non défini'}`}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
      />
    );
  };

  if (loading && !environment) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center">
          <div className="text-gray-500">Chargement...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/environments')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <FiArrowLeft size={20} />
            Retour aux environnements
          </button>

          <div className="flex items-center gap-4 mb-4">
            {environment && (
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
                style={{
                  backgroundColor: `${environment.color || '#10B981'}20`,
                  borderLeft: `4px solid ${environment.color || '#10B981'}`
                }}
              >
                {environment.type === 'prod' ? '🚀' : environment.type === 'dev' ? '🧪' : '📋'}
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Variables - {environment?.displayName || environment?.name}
              </h1>
              <p className="text-gray-600 mt-1">
                Configurez les surcharges de variables pour cet environnement
              </p>
            </div>
          </div>

          {/* Architecture selector */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Architecture
            </label>
            <select
              value={selectedArchitectureId}
              onChange={(e) => setSelectedArchitectureId(e.target.value)}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
            >
              {architectures.map(arch => (
                <option key={arch.id} value={arch.id}>
                  {arch.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Info banner */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <FiInfo className="text-blue-600 mt-0.5 flex-shrink-0" size={20} />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">À propos des overrides d'environnement</p>
            <p>
              Les overrides permettent de surcharger les valeurs par défaut des paramètres pour cet environnement spécifique.
              Laissez vide pour utiliser la valeur par défaut de l'architecture.
            </p>
          </div>
        </div>

        {/* Parameters list */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-500">Chargement des paramètres...</div>
          </div>
        ) : parameters.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
            <FiInfo size={64} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Aucun paramètre
            </h3>
            <p className="text-gray-500">
              Cette architecture ne contient aucun paramètre configurable.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {parameters.map(param => (
              <div
                key={param.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {param.display_name || param.name}
                      </h3>
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                        {param.type}
                      </span>
                      {param.hasOverride && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center gap-1">
                          <FiEdit2 size={10} />
                          Override actif
                        </span>
                      )}
                      {param.is_sensitive && (
                        <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                          Sensible
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{param.description || 'Aucune description'}</p>

                    {param.default_value?.value !== undefined && (
                      <div className="text-sm text-gray-500 mb-3">
                        <span className="font-medium">Valeur par défaut:</span> {JSON.stringify(param.default_value.value)}
                      </div>
                    )}

                    {param.default_value?.expression && (
                      <div className="text-sm text-purple-600 mb-3 font-mono bg-purple-50 px-2 py-1 rounded">
                        <span className="font-medium">Expression:</span> {param.default_value.expression}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Valeur pour {environment?.displayName || environment?.name}
                  </label>

                  {renderValueInput(param)}

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSaveOverride(param.id)}
                      disabled={saving || (editingValues[param.id] === undefined && !param.hasOverride)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FiSave size={16} />
                      {param.hasOverride ? 'Mettre à jour' : 'Créer override'}
                    </button>

                    {param.hasOverride && (
                      <button
                        onClick={() => handleRemoveOverride(param.id)}
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition disabled:opacity-50"
                      >
                        <FiX size={16} />
                        Supprimer override
                      </button>
                    )}

                    {!param.hasOverride && editingValues[param.id] !== undefined && (
                      <button
                        onClick={() => {
                          setEditingValues(prev => {
                            const updated = { ...prev };
                            delete updated[param.id];
                            return updated;
                          });
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                      >
                        <FiRefreshCw size={16} />
                        Réinitialiser
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EnvironmentVariablesPage;
